import React from "react";

/**
 * Tints the iOS 26+ Safari top ("forehead") and bottom ("chin") toolbars.
 *
 * Safari 26 (Liquid Glass) IGNORES the `theme-color` meta tag. Instead it
 * samples the `background-color` of `position: fixed`/`sticky` elements at the
 * viewport edges — but only real, on-top elements (it won't sample something
 * hidden behind content with a negative z-index). Verified on-device: an opaque
 * fixed strip at the top/bottom edge tints the adjacent Safari bar to match.
 *
 * So we render two thin fixed strips pinned to the top and bottom edges. Their
 * color blends into the dark editor/player background while tinting the Safari
 * bars. Pass `color` to match it to the room (see App.js). Requires
 * `viewport-fit=cover` in index.html so the safe-area insets resolve.
 */
/**
 * The tint strips only serve a purpose on iOS Safari, where the toolbars sample
 * an on-top fixed element's background. On desktop (and any non-iOS browser)
 * they have no such behavior and just render as dark bars over the page — so we
 * render nothing there. iPadOS 13+ reports as "MacIntel", so we also treat a
 * touch-capable Mac as iOS.
 */
const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return (
    /iP(hone|ad|od)/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

const edgeStyle = (edge, color) => ({
  position: "fixed",
  left: 0,
  right: 0,
  [edge]: 0,
  // A small non-zero height is required — a 0-height strip is not sampled.
  // Also covers the safe-area inset (notch / home indicator) when present.
  height: `calc(env(safe-area-inset-${edge}) + 12px)`,
  backgroundColor: color,
  zIndex: 2147483647,
  pointerEvents: "none",
});

export default function SafariBarTint({ color = "#1C1C1C" }) {
  if (!isIOS()) return null;
  return (
    <>
      <div aria-hidden="true" style={edgeStyle("top", color)} />
      <div aria-hidden="true" style={edgeStyle("bottom", color)} />
    </>
  );
}
